import React, { useState, useEffect } from 'react'
import { api } from '../services/authService'
import toast from 'react-hot-toast'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  CameraIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'

const CATEGORIES = [
  { id: 'pintura_frio', label: 'Pintura a Frio', icon: '🎨' },
  { id: 'munck', label: 'Munck / Guindaste', icon: '🏗️' },
  { id: 'bate_estaca', label: 'Bate-Estaca', icon: '⚙️' },
  { id: 'carroceria', label: 'Carroceria / Flatbed', icon: '🚛' },
  { id: 'all', label: 'Genérico (todos)', icon: '📋' },
]

const ROLE_ORDER = ['saida', 'devolucao', 'periodica']

const ROLE_INFO = {
  saida: { label: 'Saída', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: '🚀' },
  devolucao: { label: 'Devolução', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: '🔄' },
  periodica: { label: 'Periódica', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: '📅' },
}

const FIELD_ICONS = {
  photo: '📷',
  checkbox: '☑️',
  select: '🔽',
  number: '🔢',
  textarea: '📝',
  text: '📄',
}

export default function InspectionTemplatesPage() {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState('pintura_frio')
  const [expandedTemplate, setExpandedTemplate] = useState(null)
  const [templateDetails, setTemplateDetails] = useState({})
  const [loadingDetails, setLoadingDetails] = useState({})

  useEffect(() => {
    fetchTemplates()
    setExpandedTemplate(null)
  }, [activeCategory])

  const fetchTemplates = async () => {
    setLoading(true)
    try {
      const response = await api.get('/api/inspections/templates', {
        params: { vehicle_category: activeCategory }
      })
      setTemplates(response.data.data || [])
    } catch {
      toast.error('Erro ao carregar templates')
    } finally {
      setLoading(false)
    }
  }

  const fetchTemplateDetails = async (templateId) => {
    if (templateDetails[templateId]) return
    setLoadingDetails(prev => ({ ...prev, [templateId]: true }))
    try {
      const response = await api.get(`/api/inspections/templates/${templateId}`)
      setTemplateDetails(prev => ({ ...prev, [templateId]: response.data.data }))
    } catch {
      toast.error('Erro ao carregar detalhes')
    } finally {
      setLoadingDetails(prev => ({ ...prev, [templateId]: false }))
    }
  }

  const toggleTemplate = (templateId) => {
    if (expandedTemplate === templateId) {
      setExpandedTemplate(null)
    } else {
      setExpandedTemplate(templateId)
      fetchTemplateDetails(templateId)
    }
  }

  const handleToggleActive = async (e, templateId, currentActive) => {
    e.stopPropagation()
    try {
      await api.put(`/api/inspections/templates/${templateId}/toggle-active`)
      toast.success(currentActive ? 'Template desativado' : 'Template ativado')
      fetchTemplates()
    } catch {
      toast.error('Erro ao alterar status')
    }
  }

  // Sort templates by role order
  const sortedTemplates = [...templates].sort((a, b) => {
    const ia = ROLE_ORDER.indexOf(a.inspection_role)
    const ib = ROLE_ORDER.indexOf(b.inspection_role)
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
  })

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Templates de Vistoria</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Checklists por tipo de equipamento — gerados automaticamente ao ativar contratos
        </p>
      </div>

      {/* Info Banner */}
      <div className="flex gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-6 text-sm text-blue-800 dark:text-blue-300">
        <WrenchScrewdriverIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>
          Ao ativar um contrato, o sistema cria 3 vistorias com o checklist correto para o equipamento:
          {' '}<strong>Saída</strong> (entrega), <strong>Devolução</strong> (7 dias antes do fim)
          e <strong>Periódica</strong> (a cada 30 dias).
        </span>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeCategory === cat.id
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <span>{cat.icon}</span>
            <span>{cat.label}</span>
          </button>
        ))}
      </div>

      {/* Templates Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          <div className="col-span-1">Tipo</div>
          <div className="col-span-5">Nome do Template</div>
          <div className="col-span-2">Fotos</div>
          <div className="col-span-2">Assinatura</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1"></div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : sortedTemplates.length === 0 ? (
          <div className="py-10 text-center text-gray-400 dark:text-gray-500">
            <DocumentTextIcon className="h-10 w-10 mx-auto mb-2" />
            <p>Nenhum template encontrado para esta categoria</p>
          </div>
        ) : (
          <div>
            {sortedTemplates.map(template => {
              const roleInfo = ROLE_INFO[template.inspection_role] || { label: '—', color: 'bg-gray-100 text-gray-500', icon: '📋' }
              const isExpanded = expandedTemplate === template.id
              const details = templateDetails[template.id]
              const isLoadingDetails = loadingDetails[template.id]

              return (
                <div key={template.id} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                  {/* Row */}
                  <div
                    className="grid grid-cols-12 gap-3 px-4 py-3 items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                    onClick={() => toggleTemplate(template.id)}
                  >
                    {/* Role */}
                    <div className="col-span-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${roleInfo.color}`}>
                        {roleInfo.icon} {roleInfo.label}
                      </span>
                    </div>

                    {/* Name */}
                    <div className="col-span-5">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{template.name}</p>
                      {template.description && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{template.description}</p>
                      )}
                    </div>

                    {/* Photos */}
                    <div className="col-span-2 text-sm text-gray-600 dark:text-gray-400">
                      {template.requires_photos ? (
                        <span className="flex items-center gap-1">
                          <CameraIcon className="h-3.5 w-3.5" />
                          {template.min_photos}–{template.max_photos}
                        </span>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </div>

                    {/* Signature */}
                    <div className="col-span-2 text-xs text-gray-500 dark:text-gray-400">
                      {template.requires_client_signature ? '✍️ Cliente' :
                       template.requires_signature ? '✍️ Sim' :
                       <span className="text-gray-300 dark:text-gray-600">—</span>}
                    </div>

                    {/* Active status */}
                    <div className="col-span-1">
                      <button
                        onClick={(e) => handleToggleActive(e, template.id, template.active)}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                          template.active
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                        }`}
                      >
                        {template.active
                          ? <><CheckCircleIcon className="h-3 w-3" /> Ativo</>
                          : <><XCircleIcon className="h-3 w-3" /> Inativo</>
                        }
                      </button>
                    </div>

                    {/* Expand */}
                    <div className="col-span-1 flex justify-end text-gray-400">
                      {isExpanded
                        ? <ChevronDownIcon className="h-4 w-4" />
                        : <ChevronRightIcon className="h-4 w-4" />
                      }
                    </div>
                  </div>

                  {/* Expanded: sections + items */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-850 px-4 py-4">
                      {isLoadingDetails ? (
                        <div className="flex justify-center py-4">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        </div>
                      ) : details ? (
                        <div className="space-y-3">
                          {(details.sections || []).map((section, sIdx) => (
                            <div key={section.id || sIdx}>
                              {/* Section title */}
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                                {section.name} <span className="font-normal normal-case">({(section.items || []).length} itens)</span>
                              </p>
                              {/* Items list */}
                              <div className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                                {(section.items || []).map((item, iIdx) => (
                                  <div key={item.id || iIdx} className="flex items-center gap-3 px-3 py-2 text-sm">
                                    <span className="text-base w-5 flex-shrink-0">{FIELD_ICONS[item.field_type] || '📄'}</span>
                                    <span className="flex-1 text-gray-800 dark:text-gray-200">{item.name}</span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                                      item.is_required
                                        ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                                        : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                                    }`}>
                                      {item.is_required ? 'Obrigatório' : 'Opcional'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 text-center py-3">Sem detalhes disponíveis</p>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
        Os templates são vinculados ao veículo pelo campo <strong>Categoria de Vistoria</strong> no cadastro do veículo.
        Veículos sem categoria usam os templates genéricos (<em>all</em>).
      </p>
    </div>
  )
}
